import { useEffect, useState } from "react";
// components
import CardCustomTest from "components/Cards/CardCustomTest.js";
import CardDesempenoEspecialidad from "components/Cards/CardDesempenoEspecialidad.js";
import CardCustomPractica from "components/Cards/CardCustomPractica.js";
import CardCustomFlashcards from "components/Cards/CardCustomFlashcards.js";
import CardPlanEstudio from "components/Cards/CardPlanEstudio.js";
import CardMisRetos from "components/Cards/CardMisRetos.js";
import RecentActivity from "components/Custom/RecentActivity.js";

import { useHistory } from "react-router-dom";
import { getSectionPerformance, getSpecialtyPerformancePageData } from "utils/supabase";
import { ensureMinimumSkeletonDuration } from "utils/loading";

export default function Dashboard() {
  const [sectionPerformancePayload, setSectionPerformancePayload] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    let mounted = true;

    async function fetchSectionPerformance() {
      try {
        const [sectionData, specialtyResult] = await Promise.all([
          getSectionPerformance(),
          getSpecialtyPerformancePageData(30, false),
        ]);

        if (!mounted) return;

        if (specialtyResult?.error) {
          console.error("Error fetching specialty performance:", specialtyResult.error);
        }

        const sectionRows = Array.isArray(sectionData?.items) ? sectionData.items : [];
        const accordionSections = Array.isArray(specialtyResult?.data?.accordion_sections)
          ? specialtyResult.data.accordion_sections
          : [];
        // match sections by normalized name since the two RPCs return independent payloads
        const sectionsByName = new Map(
          accordionSections.map((section) => [String(section?.section_name || "").trim().toLowerCase(), section])
        );

        const normalizedItems = sectionRows.flatMap((row) => {
          const sectionName = row?.section || "Sin sección";
          const matchedSection = sectionsByName.get(String(sectionName).trim().toLowerCase());
          const specialties = Array.isArray(matchedSection?.specialties) ? matchedSection.specialties : [];

          if (specialties.length === 0) {
            return [{
              specialty_section: sectionName,
              specialty_name: sectionName,
              percentage: Number(row?.percentage) || 0,
              correct_answers: Number(row?.correct_answers) || 0,
              total_questions: Number(row?.answered_questions) || 0,
            }];
          }

          return specialties.map((specialty) => ({
            specialty_section: sectionName,
            specialty_name: specialty?.specialty_name || "Sin nombre",
            percentage: Number(specialty?.accuracy_percentage) || 0,
            correct_answers: Number(specialty?.correct_items) || 0,
            total_questions: Number(specialty?.total_items) || 0,
          }));
        });

        setSectionPerformancePayload({
          ...(sectionData || {}),
          items: normalizedItems,
        });
      } catch (error) {
        console.error("Error fetching section performance:", error);
      }
    }

    async function loadDashboard() {
      setIsLoading(true);
      const loadStartedAt = Date.now();
      await Promise.allSettled([fetchSectionPerformance()]);
      await ensureMinimumSkeletonDuration(loadStartedAt);

      if (mounted) {
        setIsLoading(false);
      }
    }

    loadDashboard();

    return () => {
      mounted = false;
    };
  }, []);

  const handleGoExamenes = (e) => {
    e.preventDefault();
    history.push("/admin/examenes");
  };
  const handleGoFlashcards = (e) => {
    e.preventDefault();
    history.push("/admin/flashcards");
  };

  return (
    <>
      <style>
        {`
          .dashboard-equal-row {
            display: grid;
            grid-template-columns: minmax(0, 1fr);
            gap: 1rem;
            align-items: stretch;
            width: 100%;
          }

          .dashboard-equal-card {
            display: flex;
            min-width: 0;
            align-self: stretch;
          }

          .dashboard-equal-card > * {
            flex: 1 1 auto;
            width: 100%;
            height: 100%;
            margin-bottom: 0 !important;
          }

          @media (max-width: 767px) {
            .mobile-dashboard .dashboard-feature-card {
              border: 1px solid #d5e1f0 !important;
              border-radius: 18px !important;
              box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05) !important;
            }
          }

          @media (min-width: 1024px) {
            .dashboard-equal-row {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }
          }
        `}
      </style>
      <div className="mobile-dashboard flex flex-wrap mt-0 sm:mt-1 md:mt-2 items-stretch gap-y-3 sm:gap-y-4">
        <div className="w-full md:w-12/12 lg:w-4/12 px-0 sm:px-4 flex flex-wrap h-full">
          <div className="w-full h-full flex flex-col">
            <div className="flex-1">
              <CardCustomTest onClick={(e) => handleGoExamenes(e)} loading={isLoading} />
            </div>
          </div>
        </div>
        <div className="w-full md:w-6/12 lg:w-4/12 px-0 sm:px-4 flex flex-wrap h-full">
          <div className="w-full h-full flex flex-col">
            <div className="flex-1">
              <CardCustomPractica loading={isLoading} />
            </div>
          </div>
        </div>
        <div className="w-full md:w-6/12 lg:w-4/12 md:mx-auto lg:mx-0 px-0 sm:px-4 flex flex-wrap h-full">
          <div className="w-full h-full flex flex-col">
            <div className="flex-1">
              <CardCustomFlashcards onClick={(e) => handleGoFlashcards(e)} loading={isLoading} />
            </div>
          </div>
        </div>
        <div className="w-full px-0 sm:px-4">
          <div className="dashboard-equal-row">
            <div className="dashboard-equal-card">
              <CardPlanEstudio loading={isLoading} />
            </div>
            <div className="dashboard-equal-card">
              <RecentActivity />
            </div>
          </div>
        </div>
        <div className="w-full px-0 sm:px-4">
          <div className="dashboard-equal-row">
            <div className="dashboard-equal-card">
              <CardMisRetos />
            </div>
            <div className="dashboard-equal-card">
              <CardDesempenoEspecialidad
                items={sectionPerformancePayload?.items}
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
